import type { FC, ReactNode } from 'react'

import type {
    Bag,
    Brand,
    BrandId,
    Product,
    ProgramContent,
    SessionUser
} from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { useRouter } from 'next/router'
import { flushSync } from 'react-dom'
import { apiFetch } from '@/lib/client'

import {
    BRANDS,
    BRAND_STORAGE_KEY,
    DEFAULT_BRAND
} from '@/constants/brands'

const BAG_STORAGE_KEY = 'hajime-bag'

/* The ebook a guest tried to add, kept while they sign in so it lands in their bag afterwards. */
const PENDING_STORAGE_KEY = 'hajime-pending-add'

interface SiteContextValue {
    brand: Brand
    setBrand: (id: BrandId) => void
    catalog: Product[]
    user: SessionUser | null
    userLoaded: boolean
    owned: string[]
    isOwned: (id: string) => boolean
    refreshUser: () => Promise<SessionUser | null>
    logout: () => Promise<void>
    bag: Bag
    bagReady: boolean
    bagCount: number
    bagProducts: Product[]
    bump: number
    inBag: (id: string) => boolean
    addToBag: (id: string) => void
    removeFromBag: (id: string) => void
    clearBag: () => void
    motionPaused: boolean
    toggleMotion: () => void
}

interface SiteProviderProps {
    catalog?: Product[]
    programs?: ProgramContent[]
    initialUser?: SessionUser | null
    children: ReactNode
}

type MeResponse = { user: SessionUser | null, owned: string[] }

const SiteContext = createContext<SiteContextValue | null>(null)

const isBrandId = (value: string | null): value is BrandId => {
    return value === 'jlpt' || value === 'jft'
}

const readSavedBag = (): string[] => {
    try {
        const parsed: unknown = JSON.parse(localStorage.getItem(BAG_STORAGE_KEY) ?? '[]')

        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string').slice(0, 50) : []
    }
    catch {
        return []
    }
}

const takePending = (): string[] => {
    try {
        const pending = localStorage.getItem(PENDING_STORAGE_KEY)

        localStorage.removeItem(PENDING_STORAGE_KEY)

        return pending && /^[a-z0-9-]{1,64}$/i.test(pending) ? [pending] : []
    }
    catch {
        return []
    }
}

/* Swaps the program's editable texts in from the database when the page loaded them. */
const withContent = (base: Brand, products: Product[], content?: ProgramContent): Brand => {
    if (!content) return { ...base, products }

    return {
        ...base,
        programId: content.id,
        products,
        catalog: {
            title: content.catalogTitle || base.catalog.title,
            description: content.catalogDescription || base.catalog.description
        },
        story: {
            ...base.story,
            title: content.storyTitle || base.story.title,
            paragraphs: [content.storyParagraph1, content.storyParagraph2],
            badgeValue: content.storyBadgeValue,
            badgeLabel: content.storyBadgeLabel
        }
    }
}

/*
 * Site-wide state, mounted once in _app: the active program (JLPT or JFT),
 * the catalog, the signed-in user and what they own, the bag and the "pause
 * motion" switch. Guests keep the bag in localStorage; signed-in users get it
 * saved on their account and merged on sign-in. The program and motion
 * choices are mirrored to <html data-brand> and <html data-motion>.
 */
export const SiteProvider: FC<SiteProviderProps> = ({
    catalog: pageCatalog,
    programs,
    initialUser,
    children
}): ReactNode => {
    const [brandId, setBrandId] = useState<BrandId>(DEFAULT_BRAND)
    const [fetchedCatalog, setFetchedCatalog] = useState<Product[] | null>(null)
    const [user, setUser] = useState<SessionUser | null>(initialUser ?? null)
    const [userLoaded, setUserLoaded] = useState(initialUser !== undefined)
    const [owned, setOwned] = useState<string[]>([])
    const [bag, setBag] = useState<Bag>([])
    const [bump, setBump] = useState(0)
    const [motionPaused, setMotionPaused] = useState(false)
    const [bagReady, setBagReady] = useState(false)

    const router = useRouter()
    const userRef = useRef<SessionUser | null>(initialUser ?? null)
    const userLoadedRef = useRef(initialUser !== undefined)
    const userPromiseRef = useRef<Promise<SessionUser | null> | null>(null)

    const catalog = useMemo(() => pageCatalog ?? fetchedCatalog ?? [], [pageCatalog, fetchedCatalog])

    /*
     * Loads the user, then merges the bag saved in this browser, plus the
     * ebook they picked before signing in, into their account.
     */
    const refreshUser = useCallback((): Promise<SessionUser | null> => {
        const run = async (): Promise<SessionUser | null> => {
            const me = await apiFetch<MeResponse>('/api/auth/me')
            const nextUser = me.ok ? me.data.user : null

            userRef.current = nextUser
            userLoadedRef.current = true
            setUser(nextUser)
            setOwned(me.ok ? me.data.owned : [])
            setUserLoaded(true)

            if (nextUser) {
                const pending = takePending()
                const merged = await apiFetch<{ productIds: string[] }>('/api/cart', {
                    method: 'POST',
                    body: { action: 'merge', productIds: [...readSavedBag(), ...pending] }
                })

                if (merged.ok) setBag(merged.data.productIds)
                if (pending.length > 0) setBump((value) => value + 1)
            }

            return nextUser
        }

        const promise = run()

        userPromiseRef.current = promise

        return promise
    }, [])

    useEffect(() => {
        try {
            const saved = localStorage.getItem(BRAND_STORAGE_KEY)

            if (isBrandId(saved)) setBrandId(saved)
        }
        catch {
            // Storage can be unavailable (private mode); keep the default program.
        }

        setBag(readSavedBag())
        setBagReady(true)

        void refreshUser()
    }, [refreshUser])

    /* Mirrors the bag to this browser, only after the saved one was read back first. */
    useEffect(() => {
        if (!bagReady) return

        try {
            localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(bag))
        }
        catch {
            // The bag still works for this visit without storage.
        }
    }, [bag, bagReady])

    useEffect(() => {
        if (pageCatalog || fetchedCatalog) return

        let active = true

        void apiFetch<{ products: Product[] }>('/api/catalog').then((result) => {
            if (active && result.ok) setFetchedCatalog(result.data.products)
        })

        return () => {
            active = false
        }
    }, [pageCatalog, fetchedCatalog])

    useEffect(() => {
        document.documentElement.dataset.brand = brandId
    }, [brandId])

    /* Turns on scroll reveals (see globals.css); skipped for reduced motion. */
    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

        const root = document.documentElement

        root.classList.add('kuro-reveal')

        return () => root.classList.remove('kuro-reveal')
    }, [])

    useEffect(() => {
        document.documentElement.dataset.motion = motionPaused ? 'paused' : 'running'
    }, [motionPaused])

    /*
     * Switching program crossfades the whole page with the View Transitions
     * API (one GPU snapshot, no per-element transitions), and switches
     * instantly where the browser has no support or motion is reduced.
     */
    const setBrand = useCallback((id: BrandId) => {
        const apply = () => {
            flushSync(() => setBrandId(id))
            document.documentElement.dataset.brand = id
        }

        const transitions = document as Document & { startViewTransition?: (callback: () => void) => unknown }
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (transitions.startViewTransition && !reduced) transitions.startViewTransition(apply)
        else apply()

        try {
            localStorage.setItem(BRAND_STORAGE_KEY, id)
        }
        catch {
            // Remembering the choice is a nicety; the switch still works without it.
        }
    }, [])

    const isOwned = useCallback((id: string) => owned.includes(id), [owned])

    const inBag = useCallback((id: string) => bag.includes(id), [bag])

    const syncCart = useCallback((body: Record<string, unknown>) => {
        if (!userRef.current) return

        void apiFetch('/api/cart', { method: 'POST', body })
    }, [])

    /*
     * Buying needs an account, so a guest is sent to sign in first; the
     * ebook they picked is remembered and added once they are signed in.
     */
    const addToBag = useCallback((id: string) => {
        if (owned.includes(id)) return

        const proceed = (current: SessionUser | null) => {
            if (!current) {
                try {
                    localStorage.setItem(PENDING_STORAGE_KEY, id)
                }
                catch {
                    // They can still add it again after signing in.
                }

                const back = router.asPath.split('#')[0] || '/'

                void router.push(`/masuk?alasan=keranjang&next=${encodeURIComponent(back === '/' ? '/#catalog' : back)}`)
                return
            }

            setBag((previous) => (previous.includes(id) ? previous : [...previous, id]))
            setBump((value) => value + 1)
            syncCart({ action: 'add', productId: id })
        }

        if (userLoadedRef.current) proceed(userRef.current)
        else void (userPromiseRef.current ?? refreshUser()).then(proceed)
    }, [owned, refreshUser, router, syncCart])

    const removeFromBag = useCallback((id: string) => {
        setBag((previous) => previous.filter((item) => item !== id))
        syncCart({ action: 'remove', productId: id })
    }, [syncCart])

    const clearBag = useCallback(() => {
        setBag([])
        syncCart({ action: 'clear' })
    }, [syncCart])

    const logout = useCallback(async () => {
        await apiFetch('/api/auth/logout', { method: 'POST', body: {} })

        userRef.current = null
        setUser(null)
        setOwned([])
        setBag([])

        try {
            localStorage.removeItem(BAG_STORAGE_KEY)
            localStorage.removeItem(PENDING_STORAGE_KEY)
        }
        catch {
            // Nothing to clear.
        }
    }, [])

    const toggleMotion = useCallback(() => {
        setMotionPaused((value) => !value)
    }, [])

    const brand = useMemo(() => {
        const products = catalog.filter((product) => product.program === brandId)

        return withContent(BRANDS[brandId], products, programs?.find((program) => program.code === brandId))
    }, [brandId, catalog, programs])

    const bagProducts = useMemo(() => {
        return bag.map((id) => catalog.find((product) => product.id === id)).filter((product): product is Product => Boolean(product))
    }, [bag, catalog])

    const value = useMemo<SiteContextValue>(() => ({
        brand,
        setBrand,
        catalog,
        user,
        userLoaded,
        owned,
        isOwned,
        refreshUser,
        logout,
        bag,
        bagReady,
        bagCount: catalog.length > 0 ? bagProducts.length : bag.length,
        bagProducts,
        bump,
        inBag,
        addToBag,
        removeFromBag,
        clearBag,
        motionPaused,
        toggleMotion
    }), [
        brand,
        setBrand,
        catalog,
        user,
        userLoaded,
        owned,
        isOwned,
        refreshUser,
        logout,
        bag,
        bagReady,
        bagProducts,
        bump,
        inBag,
        addToBag,
        removeFromBag,
        clearBag,
        motionPaused,
        toggleMotion
    ])

    return (
        <SiteContext.Provider value={value}>
            {children}
        </SiteContext.Provider>
    )
}

export const useSite = (): SiteContextValue => {
    const context = useContext(SiteContext)

    if (!context) throw new Error('useSite must be used inside SiteProvider')

    return context
}

/* Safe outside the provider (shared layout pieces): falls back to the default program. */
export const useBrand = (): Brand => {
    const context = useContext(SiteContext)

    return context?.brand ?? BRANDS[DEFAULT_BRAND]
}
